import { useState } from 'react';
import Swal from 'sweetalert2';
import { Card, CardBody, Badge, Spinner, Alert, Button, Input, Switch } from '@components/ui';
import { useCompanySettings } from '@features/company/hooks/useCompany';
import { useCertificates } from '../hooks/useCertificates';
import { certificateService } from '../services/certificateService';
import { CheckCircleIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';

export function FiscalInfoTab() {
  const queryClient = useQueryClient();
  const { data: companyData, isLoading, error } = useCompanySettings();
  const { data: certificatesData, isLoading: isLoadingCerts, error: certsError } = useCertificates();
  const [useCustomJwt, setUseCustomJwt] = useState(false);
  const [secretJwt, setSecretJwt] = useState('');
  const [jwtError, setJwtError] = useState<string | null>(null);
  const [certificateKey, setCertificateKey] = useState('');

  // Minimum length for a valid JWT/Secret token (usually 100+ chars, but we use 50 to be safe)
  const MIN_JWT_LENGTH = 50;

  const handleJwtChange = (value: string) => {
    setSecretJwt(value);
    if (value && value.length < MIN_JWT_LENGTH) {
      setJwtError(`El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres (actualmente: ${value.length}). Si no tienes el JWT, desactiva esta opción.`);
    } else {
      setJwtError(null);
    }
  };
  const [authCertFile, setAuthCertFile] = useState<File | null>(null);
  const [sigCertFile, setSigCertFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isFirmatech, setIsFirmatech] = useState(false);

  // Debug logs
  console.log('🔍 FiscalInfoTab - certificatesData:', certificatesData);
  console.log('🔍 FiscalInfoTab - isLoadingCerts:', isLoadingCerts);
  console.log('🔍 FiscalInfoTab - certsError:', certsError);
  console.log('🔍 FiscalInfoTab - companyData:', companyData);

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error al cargar la información de la compañía. Por favor, intente nuevamente.
      </Alert>
    );
  }

  if (!companyData) {
    return (
      <Card>
        <CardBody>
          <div className="text-center py-8">
            <p className="text-secondary-600 dark:text-secondary-400">
              No hay una compañía seleccionada.
            </p>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Check if certificates exist from API or from company data
  const apiCertificate = certificatesData?.certificate;
  const hasCertificates = apiCertificate?.certificateAuth && apiCertificate?.certificateSig;

  console.log('🔍 FiscalInfoTab - apiCertificate:', apiCertificate);
  console.log('🔍 FiscalInfoTab - hasCertificates:', hasCertificates);

  const handleSaveCertificates = async () => {
    if (!authCertFile || (!isFirmatech && !sigCertFile) || !certificateKey) {
      setUploadError('Por favor complete todos los campos requeridos');
      return;
    }

    if (useCustomJwt && !secretJwt) {
      setUploadError('Por favor ingrese el Secret JWT o desactive la opción');
      return;
    }

    if (useCustomJwt && secretJwt && secretJwt.length < MIN_JWT_LENGTH) {
      setUploadError(`El JWT debe tener al menos ${MIN_JWT_LENGTH} caracteres. Si no tienes el JWT, desactiva la opción.`);
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);

      await certificateService.uploadCertificates({
        authCertFile,
        signCertFile: isFirmatech ? authCertFile : sigCertFile!, // Si es Firmatech, usar el mismo certificado
        secretJwt: useCustomJwt ? secretJwt : null,
        certificatePassword: certificateKey,
      });

      // Invalidate certificates query to refetch
      await queryClient.invalidateQueries({ queryKey: ['certificates'] });

      // Show success alert
      await Swal.fire({
        icon: 'success',
        title: 'Certificados subidos exitosamente',
        text: 'Los certificados digitales han sido configurados correctamente.',
        confirmButtonColor: '#10b981',
      });

      // Clear form
      setAuthCertFile(null);
      setSigCertFile(null);
      setSecretJwt('');
      setCertificateKey('');
      setUseCustomJwt(false);
    } catch (error: any) {
      console.error('Error uploading certificates:', error);

      // Check if it's an A400 validation error
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al subir los certificados';
      setUploadError(errorMessage);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Company Info */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              Información de la Compañía
            </h2>
            <Badge variant={hasCertificates ? 'success' : 'warning'}>
              {hasCertificates ? 'Certificados Configurados' : 'Certificados Pendientes'}
            </Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Company ID:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1 break-all">
                {companyData.id || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Company Code:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1 break-all">
                {companyData.code || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Razón Social:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.fullName || companyData.name || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Nombre Comercial:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.tradeName || companyData.name || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">RUC:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.numberId || companyData.ruc || '-'}
              </p>
            </div>

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">DV:</span>
              <p className="text-secondary-900 dark:text-white font-medium mt-1">
                {companyData.dv || '-'}
              </p>
            </div>

            {companyData.taxRegime && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Régimen Fiscal:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.taxRegime}
                </p>
              </div>
            )}

            {companyData.economicActivity && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Actividad Económica:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.economicActivity}
                </p>
              </div>
            )}

            {companyData.website && (
              <div>
                <span className="text-secondary-600 dark:text-secondary-400">Sitio Web:</span>
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  <a
                    href={companyData.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                  >
                    {companyData.website}
                  </a>
                </p>
              </div>
            )}

            <div>
              <span className="text-secondary-600 dark:text-secondary-400">Tipo de Contribuyente:</span>
              {companyData.typeTaxpayer ? (
                <p className="text-secondary-900 dark:text-white font-medium mt-1">
                  {companyData.typeTaxpayer === 1 ? 'Persona Natural' : 'Persona Jurídica'}
                </p>
              ) : (
                <p className="text-warning-600 dark:text-warning-400 text-sm mt-1">
                  No configurado - <a href="/company/edit" className="underline hover:text-warning-700">actualiza tu tipo de contribuyente</a>
                </p>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      {/* Certificates Section */}
      <Card>
        <CardBody>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-secondary-900 dark:text-white">
              Certificados Digitales
            </h2>
            {apiCertificate?.status !== null && (
              <Badge variant={apiCertificate?.status ? 'success' : 'danger'}>
                {apiCertificate?.status ? 'Activo' : 'Inactivo'}
              </Badge>
            )}
          </div>

          {isLoadingCerts ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : hasCertificates ? (
            /* Show existing certificates */
            <div className="space-y-6">
              <Alert variant="success">
                Los certificados digitales están configurados correctamente.
              </Alert>

              {/* Certificate Names */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="primary">Autenticación</Badge>
                  </div>
                  <p className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                    {apiCertificate?.certificateAuth}
                  </p>
                </div>

                <div className="border border-secondary-200 dark:border-secondary-700 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="primary">Firma</Badge>
                  </div>
                  <p className="text-sm text-secondary-900 dark:text-white font-mono break-all">
                    {apiCertificate?.certificateSig}
                  </p>
                </div>
              </div>

              {/* Secret JWT */}
              {apiCertificate?.hasSecretJwt && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Secret JWT
                  </label>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="success">Configurado</Badge>
                  </div>
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Token secreto para la autenticación JWT (no visible por seguridad)
                  </p>
                </div>
              )}

              {/* Certificate Key - Encrypted */}
              {apiCertificate?.hasCertificateKey && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Certificate Key
                  </label>
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon className="w-5 h-5 text-success-600" />
                    <Badge variant="success">Configurada</Badge>
                  </div>
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Contraseña del certificado (no visible por seguridad)
                  </p>
                </div>
              )}

              {/* Dates */}
              {(apiCertificate?.createdDate || apiCertificate?.lastModifiedDate) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-secondary-200 dark:border-secondary-700">
                  {apiCertificate?.createdDate && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Fecha de Creación
                      </label>
                      <div className="flex items-center gap-2 text-sm text-secondary-900 dark:text-white">
                        <CalendarIcon className="w-4 h-4 text-secondary-400" />
                        {new Date(apiCertificate.createdDate).toLocaleString('es-PA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}

                  {apiCertificate?.lastModifiedDate && (
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        Última Modificación
                      </label>
                      <div className="flex items-center gap-2 text-sm text-secondary-900 dark:text-white">
                        <CalendarIcon className="w-4 h-4 text-secondary-400" />
                        {new Date(apiCertificate.lastModifiedDate).toLocaleString('es-PA', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Upload new certificates section */}
              <div className="pt-6 border-t border-secondary-200 dark:border-secondary-700">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white mb-4">
                  Actualizar Certificados
                </h3>
                <div className="space-y-6">
                  {/* Firmatech Switch */}
                  <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                    <div>
                      <p className="text-sm font-medium text-secondary-900 dark:text-white">
                        Certificado Firmatech
                      </p>
                      <p className="text-xs text-secondary-500 dark:text-secondary-400">
                        Activa esta opción si tienes un único certificado de Firmatech
                      </p>
                    </div>
                    <Switch
                      checked={isFirmatech}
                      onChange={(checked) => {
                        setIsFirmatech(checked);
                        if (checked) {
                          setSigCertFile(null); // Limpiar el segundo input si se activa
                        }
                      }}
                      size="sm"
                    />
                  </div>

                  <div className={`grid grid-cols-1 ${!isFirmatech ? 'md:grid-cols-2' : ''} gap-6`}>
                    {/* Certificate Auth Upload */}
                    <div>
                      <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                        {isFirmatech ? 'Certificado Firmatech (Autenticación y Firma) *' : 'Certificado de Autenticación *'}
                      </label>
                      <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                        <input
                          type="file"
                          accept=".pfx,.p12"
                          onChange={(e) => setAuthCertFile(e.target.files?.[0] || null)}
                          className="hidden"
                          id="authCertUpdate"
                        />
                        <label
                          htmlFor="authCertUpdate"
                          className="flex flex-col items-center cursor-pointer"
                        >
                          <svg
                            className="w-12 h-12 text-secondary-400 mb-3"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                            />
                          </svg>
                          <span className="text-sm text-secondary-600 dark:text-secondary-400">
                            {authCertFile ? authCertFile.name : 'Click para seleccionar archivo'}
                          </span>
                          <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                            .pfx o .p12
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Certificate Sig Upload - Solo si NO es Firmatech */}
                    {!isFirmatech && (
                      <div>
                        <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                          Certificado de Firma *
                        </label>
                        <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                          <input
                            type="file"
                            accept=".pfx,.p12"
                            onChange={(e) => setSigCertFile(e.target.files?.[0] || null)}
                            className="hidden"
                            id="sigCertUpdate"
                          />
                          <label htmlFor="sigCertUpdate" className="flex flex-col items-center cursor-pointer">
                            <svg
                              className="w-12 h-12 text-secondary-400 mb-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            <span className="text-sm text-secondary-600 dark:text-secondary-400">
                              {sigCertFile ? sigCertFile.name : 'Click para seleccionar archivo'}
                            </span>
                            <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                              .pfx o .p12
                            </span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Certificate Key for Update */}
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Certificate Key (Password) *
                    </label>
                    <Input
                      type="password"
                      value={certificateKey}
                      onChange={(e) => setCertificateKey(e.target.value)}
                      placeholder="Ingrese la contraseña del certificado"
                    />
                    <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                      Contraseña para desencriptar los certificados digitales
                    </p>
                  </div>

                  {/* Upload Error */}
                  {uploadError && (
                    <Alert variant="danger">
                      {uploadError}
                    </Alert>
                  )}

                  {/* Update Button */}
                  <div className="flex justify-end">
                    <Button
                      variant="primary"
                      onClick={handleSaveCertificates}
                      disabled={!authCertFile || (!isFirmatech && !sigCertFile) || !certificateKey || isUploading}
                      isLoading={isUploading}
                    >
                      {isUploading ? 'Actualizando...' : 'Actualizar Certificados'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            /* Show upload form - No certificates configured */
            <div className="space-y-6">
              <Alert variant="warning">
                No hay certificados digitales configurados. Por favor, suba los certificados
                requeridos.
              </Alert>

              {/* Firmatech Switch */}
              <div className="flex items-center justify-between p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-secondary-900 dark:text-white">
                    Certificado Firmatech
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400">
                    Activa esta opción si tienes un único certificado de Firmatech
                  </p>
                </div>
                <Switch
                  checked={isFirmatech}
                  onChange={(checked) => {
                    setIsFirmatech(checked);
                    if (checked) {
                      setSigCertFile(null);
                    }
                  }}
                  size="sm"
                />
              </div>

              <div className={`grid grid-cols-1 ${!isFirmatech ? 'md:grid-cols-2' : ''} gap-6`}>
                {/* Certificate Auth Upload */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    {isFirmatech ? 'Certificado Firmatech (Autenticación y Firma) *' : 'Certificado de Autenticación *'}
                  </label>
                  <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                    <input
                      type="file"
                      accept=".pfx,.p12"
                      onChange={(e) => setAuthCertFile(e.target.files?.[0] || null)}
                      className="hidden"
                      id="authCert"
                    />
                    <label
                      htmlFor="authCert"
                      className="flex flex-col items-center cursor-pointer"
                    >
                      <svg
                        className="w-12 h-12 text-secondary-400 mb-3"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                        />
                      </svg>
                      <span className="text-sm text-secondary-600 dark:text-secondary-400">
                        {authCertFile ? authCertFile.name : 'Click para seleccionar archivo'}
                      </span>
                      <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                        .pfx o .p12
                      </span>
                    </label>
                  </div>
                </div>

                {/* Certificate Sig Upload - Solo si NO es Firmatech */}
                {!isFirmatech && (
                  <div>
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                      Certificado de Firma *
                    </label>
                    <div className="border-2 border-dashed border-secondary-300 dark:border-secondary-600 rounded-lg p-6">
                      <input
                        type="file"
                        accept=".pfx,.p12"
                        onChange={(e) => setSigCertFile(e.target.files?.[0] || null)}
                        className="hidden"
                        id="sigCert"
                      />
                      <label htmlFor="sigCert" className="flex flex-col items-center cursor-pointer">
                        <svg
                          className="w-12 h-12 text-secondary-400 mb-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                          />
                        </svg>
                        <span className="text-sm text-secondary-600 dark:text-secondary-400">
                          {sigCertFile ? sigCertFile.name : 'Click para seleccionar archivo'}
                        </span>
                        <span className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                          .pfx o .p12
                        </span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Secret JWT and Certificate Key */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-secondary-900 dark:text-white">
                  Configuración de Certificados
                </h3>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300">
                      Usar Secret JWT personalizado
                    </label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useCustomJwt}
                        onChange={(e) => setUseCustomJwt(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-secondary-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-secondary-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-secondary-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-secondary-600 peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {useCustomJwt && (
                    <>
                      <Input
                        type="text"
                        value={secretJwt}
                        onChange={(e) => handleJwtChange(e.target.value)}
                        placeholder="Ingrese el Secret JWT (mínimo 50 caracteres)"
                        className={`font-mono text-xs ${jwtError ? 'border-danger-500' : ''}`}
                      />
                      {jwtError && (
                        <p className="mt-1 text-xs text-danger-500">
                          {jwtError}
                        </p>
                      )}
                      <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                        El JWT es un token largo (100+ caracteres) que luce así: 3A88078974CD8166E06349D1950A5FE5...
                      </p>
                    </>
                  )}

                  {!useCustomJwt && (
                    <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                      El JWT no es obligatorio por el momento. Solo actívalo si la DGI te proporcionó uno.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                    Certificate Key (Password) *
                  </label>
                  <Input
                    type="password"
                    value={certificateKey}
                    onChange={(e) => setCertificateKey(e.target.value)}
                    placeholder="Ingrese la contraseña del certificado"
                  />
                  <p className="mt-1 text-xs text-secondary-500 dark:text-secondary-400">
                    Contraseña para desencriptar los certificados digitales
                  </p>
                </div>
              </div>

              {/* Upload Error */}
              {uploadError && (
                <Alert variant="danger">
                  {uploadError}
                </Alert>
              )}

              {/* Save Button */}
              <div className="flex justify-end">
                <Button
                  variant="primary"
                  onClick={handleSaveCertificates}
                  disabled={!authCertFile || (!isFirmatech && !sigCertFile) || !certificateKey || (useCustomJwt && !secretJwt) || (useCustomJwt && !!jwtError) || isUploading}
                  isLoading={isUploading}
                >
                  {isUploading ? 'Subiendo...' : 'Guardar Certificados'}
                </Button>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      {/* Fiscal Information Section */}
      <Card>
        <CardBody>
          <h2 className="text-xl font-semibold text-secondary-900 dark:text-white mb-6">
            Información Fiscal
          </h2>
          <p className="text-secondary-600 dark:text-secondary-400">
            Información fiscal y configuraciones relacionadas con facturación electrónica.
          </p>
          {/* TODO: Add fiscal information fields */}
        </CardBody>
      </Card>
    </div>
  );
}
